// The main measurement page: settings, node picker, a single Start/Stop, a live
// view while probing (strictly one node at a time), and the full results with a
// segment-localisation ladder + verdict when more than one tier was measured.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, DEFAULT_SETTINGS } from '@/store';
import type { NodeInfo, RunResult } from '@/engine/types';
import { SegmentLadder } from '@/charts';
import { PRESETS, presetToSettings, presetById } from '@/lib/presets';
import { bytes, count, duration, estimateRunBytes, formatIntList, parseIntList, pct } from '@/lib/format';
import { settingsLink } from '@/lib/url';
import {
  CopyButton,
  EmptyState,
  Field,
  NodePicker,
  NumberInput,
  Panel,
  ProgressBar,
  TextInput,
  TierBadge,
} from './components';
import { RunReport } from './results';
import { RouteTree } from './RouteTree';
import { buildRouteTree } from '@/engine/route';

/** Settings form: presets plus the core probe parameters. Warm-up is always on. */
function SettingsForm({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const settings = useStore((s) => s.settings);
  const setSettings = useStore((s) => s.setSettings);
  const replaceSettings = useStore((s) => s.replaceSettings);
  const traceroute = useStore((s) => s.traceroute);
  const setTraceroute = useStore((s) => s.setTraceroute);
  const [preset, setPreset] = useState<string>('default');

  const applyPreset = (id: string) => {
    setPreset(id);
    const p = presetById(id);
    if (p) replaceSettings(presetToSettings(p, settings.warmupMs));
  };

  return (
    <Panel title={t('settings.title')}>
      <Field label={t('settings.preset')} htmlFor="preset">
        <select
          id="preset"
          className="input"
          value={preset}
          disabled={disabled}
          onChange={(e) => applyPreset(e.target.value)}
        >
          {PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('settings.sizes')} hint={t('settings.sizesHint')} htmlFor="sizes">
        <TextInput
          id="sizes"
          value={formatIntList(settings.sizes)}
          disabled={disabled}
          onChange={(v) => {
            setPreset('custom');
            const parsed = parseIntList(v);
            setSettings({ sizes: parsed.length > 0 ? parsed : DEFAULT_SETTINGS.sizes });
          }}
        />
      </Field>

      <div className="grid2">
        <Field label={t('settings.pps')} htmlFor="pps">
          <NumberInput
            id="pps"
            value={settings.pps}
            min={1}
            max={1000}
            disabled={disabled}
            onChange={(n) => {
              setPreset('custom');
              setSettings({ pps: n });
            }}
          />
        </Field>
        <Field label={t('settings.durationMs')} htmlFor="dur">
          <NumberInput
            id="dur"
            value={settings.durationMs}
            min={1000}
            step={1000}
            disabled={disabled}
            onChange={(n) => {
              setPreset('custom');
              setSettings({ durationMs: n });
            }}
          />
        </Field>
      </div>

      <div className="grid2">
        <Field
          label={t('settings.acceptableDelay')}
          hint={t('settings.acceptableDelayHint')}
          htmlFor="delay"
        >
          <NumberInput
            id="delay"
            value={settings.acceptableDelayMs}
            min={1}
            disabled={disabled}
            onChange={(n) => {
              setPreset('custom');
              setSettings({ acceptableDelayMs: n });
            }}
          />
        </Field>
        <Field label={t('settings.ipMode')} hint={t('settings.ipModeHint')} htmlFor="ipmode">
          <select
            id="ipmode"
            className="input"
            value={settings.ipMode}
            disabled={disabled}
            onChange={(e) => {
              setPreset('custom');
              setSettings({ ipMode: e.target.value as 'auto' | 'ipv4' | 'ipv6' });
            }}
          >
            <option value="auto">{t('settings.ipAuto')}</option>
            <option value="ipv4">IPv4</option>
            <option value="ipv6">IPv6</option>
          </select>
        </Field>
      </div>

      <label className="toggle" htmlFor="traceroute">
        <input
          id="traceroute"
          type="checkbox"
          checked={traceroute}
          disabled={disabled}
          onChange={(e) => setTraceroute(e.target.checked)}
        />
        <span className="toggle__text">
          <span className="toggle__label">{t('settings.traceroute')}</span>
          <span className="toggle__hint">{t('settings.tracerouteHint')}</span>
        </span>
      </label>

      <div className="settings__foot">
        <CopyButton
          className="btn--sm"
          text={settingsLink(settings)}
          label={t('settings.copySettingsLink')}
        />
      </div>
    </Panel>
  );
}

/** Data-usage estimate line (summed over selected nodes, both directions). */
function DataUsage({ nodeCount }: { nodeCount: number }) {
  const { t } = useTranslation();
  const settings = useStore((s) => s.settings);
  const perNode = estimateRunBytes(settings.sizes, settings.pps, settings.durationMs);
  const total = perNode * Math.max(1, nodeCount);
  return (
    <p className="datause">
      <strong>{t('settings.dataUsage')}:</strong> {bytes(total)}{' '}
      <span className="muted">
        ({count(Math.round((settings.pps * settings.durationMs) / 1000))} packets ·{' '}
        {duration(settings.durationMs)} · {t('settings.dataUsageHint')})
      </span>
    </p>
  );
}

/** Live card for a node that is currently being probed or is pending. */
function LiveNodeCard({ node, active }: { node: NodeInfo; active: boolean }) {
  const { t } = useTranslation();
  const progress = useStore((s) => s.progress[node.id]);
  const sent = progress?.sent ?? 0;
  const echoed = progress?.echoed ?? 0;
  const total = progress?.total ?? 0;
  const lossPct = sent > 0 ? ((sent - echoed) / sent) * 100 : 0;
  const frac = total > 0 ? sent / total : 0;
  return (
    <div className={`livecard${active ? ' is-active' : ''}`}>
      <div className="livecard__head">
        <TierBadge tier={node.tier} />
        <span className="livecard__name">{node.name}</span>
        <span className="livecard__state">
          {active ? t('run.current') : t('run.waiting')}
        </span>
      </div>
      {active ? (
        <>
          <ProgressBar value={frac} label={node.name} />
          <div className="livecard__counters">
            <span>
              {t('run.progress', { sent: count(sent), total: count(total || sent) })}
            </span>
            <span>
              {t('run.echoed')}: {count(echoed)}
            </span>
            <span>
              {t('metrics.totalLoss')}: {pct(lossPct)}
            </span>
          </div>
        </>
      ) : (
        <p className="muted">{t('run.waiting')}</p>
      )}
    </div>
  );
}

export function TestPage() {
  const { t } = useTranslation();
  const nodes = useStore((s) => s.nodes);
  const nodesLoading = useStore((s) => s.nodesLoading);
  const nodesError = useStore((s) => s.nodesError);
  const selected = useStore((s) => s.selectedNodeIds);
  const toggleNode = useStore((s) => s.toggleNode);
  const refreshNodes = useStore((s) => s.refreshNodes);

  const runState = useStore((s) => s.runState);
  const runOrder = useStore((s) => s.runOrder);
  const runNodes = useStore((s) => s.runNodes);
  const activeNodeId = useStore((s) => s.activeNodeId);
  const results = useStore((s) => s.results);
  const ladder = useStore((s) => s.ladder);
  const verdict = useStore((s) => s.verdict);
  const engineError = useStore((s) => s.engineError);

  const startRun = useStore((s) => s.startRun);
  const stopRun = useStore((s) => s.stopRun);
  const persistRun = useStore((s) => s.persistRun);
  const runSavedId = useStore((s) => s.runSavedId);

  const [shareLink, setShareLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isBusy = runState === 'connecting' || runState === 'running';
  const nodeById = useMemo(() => new Map(runNodes.map((n) => [n.id, n])), [runNodes]);
  const finishedRuns = useMemo(
    () => runOrder.map((id) => results[id]).filter((r): r is RunResult => !!r),
    [runOrder, results],
  );
  const multiTier = new Set(finishedRuns.map((r) => r.tier)).size > 1;
  const routeTree = useMemo(() => buildRouteTree(finishedRuns), [finishedRuns]);

  const doShare = async () => {
    setBusy(true);
    try {
      const id = await persistRun(true);
      if (id) setShareLink(`${window.location.origin}/#/share?id=${id}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page test">
      <div className="test__grid">
        <div className="test__config">
          <SettingsForm disabled={isBusy} />

          <Panel
            title={t('nodes.pick')}
            actions={
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => void refreshNodes()}
                disabled={nodesLoading || isBusy}
              >
                {t('nodes.refresh')}
              </button>
            }
          >
            <p className="hint">{t('nodes.selectTierHint')}</p>
            {nodesError ? <p className="error">{nodesError}</p> : null}
            <NodePicker nodes={nodes} selected={selected} onToggle={toggleNode} disabled={isBusy} />
            <p className="muted">{t('nodes.selectedCount', { count: selected.length })}</p>
            <DataUsage nodeCount={selected.length} />
          </Panel>

          <div className="startbar">
            {isBusy ? (
              <button type="button" className="btn btn--danger btn--lg" onClick={stopRun}>
                {t('run.stop')}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn--primary btn--lg"
                onClick={() => void startRun()}
                disabled={selected.length === 0}
              >
                {selected.length > 1
                  ? t('run.startMany', { count: selected.length })
                  : t('run.startOne')}
              </button>
            )}
            <p className="sequential-note">{t('run.sequentialNote')}</p>
            {selected.length === 0 ? <p className="muted">{t('run.noNodes')}</p> : null}
          </div>
        </div>

        <div className="test__results">
          {engineError ? <p className="error">{engineError}</p> : null}

          {runState === 'connecting' && <p className="muted">{t('common.connecting')}</p>}

          {isBusy && runOrder.length > 0 && (
            <Panel title={t('run.liveTitle')}>
              <div className="livegrid">
                {runOrder.map((id) => {
                  const node = nodeById.get(id);
                  if (!node) return null;
                  if (results[id]) {
                    return (
                      <div key={id} className="livecard is-done">
                        <div className="livecard__head">
                          <TierBadge tier={node.tier} />
                          <span className="livecard__name">{node.name}</span>
                          <span className="livecard__state">{t('run.done')}</span>
                        </div>
                        <div className="livecard__counters">
                          <span>
                            {t('metrics.totalLoss')}: {pct(results[id].metrics.totalLossPct)}
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return <LiveNodeCard key={id} node={node} active={id === activeNodeId} />;
                })}
              </div>
            </Panel>
          )}

          {runState === 'done' && finishedRuns.length > 0 && (
            <div className="results">
              <div className="results__actions">
                <button type="button" className="btn btn--primary" onClick={doShare} disabled={busy}>
                  {t('common.share')}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => void persistRun(false)}
                  disabled={runSavedId != null}
                >
                  {runSavedId != null ? t('history.saved') : t('history.save')}
                </button>
                {shareLink ? <CopyButton text={shareLink} label={t('common.copyLink')} /> : null}
              </div>

              {multiTier && ladder.length > 0 && verdict && (
                <Panel title={t('ladder.title')}>
                  <SegmentLadder rungs={ladder} verdict={verdict} />
                  <p className="hint">{t('ladder.note')}</p>
                </Panel>
              )}

              {routeTree && (
                <Panel title={t('route.title')}>
                  <p className="hint">{t('route.note')}</p>
                  <RouteTree root={routeTree} />
                </Panel>
              )}

              {finishedRuns.map((run) => (
                <RunReport key={run.nodeId} run={run} node={nodeById.get(run.nodeId)} />
              ))}
            </div>
          )}

          {runState === 'idle' && finishedRuns.length === 0 && !engineError && (
            <EmptyState>{t('run.noNodes')}</EmptyState>
          )}
        </div>
      </div>
    </div>
  );
}
